import { useEffect, useState } from "react";
import { getSessionToken } from "../../hooks/useSession";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { API_BASE } from "./types";

function PencilIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

interface EditableAgentEmailProps {
  agentId: string;
  email: string;
  onUpdated: (email: string) => void;
}

export function EditableAgentEmail({ agentId, email, onUpdated }: EditableAgentEmailProps) {
  const token = getSessionToken();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(email);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(email);
    setEditing(false);
    setError(null);
  }, [agentId, email]);

  async function save() {
    if (!token) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      setError("Email is required");
      return;
    }
    if (trimmed.toLowerCase() === email.toLowerCase()) {
      setEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/profile`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to update email");
      onUpdated(json.data.email as string);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update email");
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            type="email"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            className="sm:max-w-md"
            autoFocus
            disabled={saving}
            onKeyDown={(event) => {
              if (event.key === "Enter") void save();
              if (event.key === "Escape") {
                setDraft(email);
                setEditing(false);
                setError(null);
              }
            }}
          />
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setDraft(email);
                setEditing(false);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
        {error && <p className="text-xs text-status-rejected-text">{error}</p>}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span className="truncate">{email}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center justify-center rounded-md p-1 text-content-muted hover:text-content-primary hover:bg-surface-overlay transition-colors"
        aria-label="Edit email"
        title="Edit email"
      >
        <PencilIcon />
      </button>
    </span>
  );
}
